// Authentication state store
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi, type User, type Account } from '@/lib/api/client'

interface AuthState {
  user: User | null
  accounts: Account[]
  currentAccount: Account | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string, companyName: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  setCurrentAccount: (account: Account) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accounts: [],
      currentAccount: null,
      isLoading: true,
      isAuthenticated: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          await authApi.login(email, password)
          // After login, fetch full user data with accounts
          const result = await authApi.me()
          if (result) {
            const accounts = result.accounts || []
            const currentAccount = accounts[0] || null
            set({
              user: result.user,
              accounts,
              currentAccount,
              isAuthenticated: true,
              isLoading: false
            })
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Login failed'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      register: async (email: string, password: string, name: string, companyName: string) => {
        set({ isLoading: true, error: null })
        try {
          const { user, account } = await authApi.register(email, password, name, companyName)
          set({
            user,
            accounts: [account],
            currentAccount: account,
            isAuthenticated: true,
            isLoading: false
          })
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Registration failed'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          await authApi.logout()
        } catch {
          // Ignore logout errors
        } finally {
          set({ user: null, accounts: [], currentAccount: null, isAuthenticated: false, isLoading: false })
        }
      },

      checkAuth: async () => {
        set({ isLoading: true })
        try {
          const result = await authApi.me()
          if (result) {
            const accounts = result.accounts || []
            // Keep current account if it exists in the new accounts list, otherwise use first
            const existingCurrentAccount = get().currentAccount
            const currentAccount = existingCurrentAccount
              ? accounts.find(a => a.id === existingCurrentAccount.id) || accounts[0] || null
              : accounts[0] || null
            set({
              user: result.user,
              accounts,
              currentAccount,
              isAuthenticated: true,
              isLoading: false
            })
          } else {
            set({ user: null, accounts: [], currentAccount: null, isAuthenticated: false, isLoading: false })
          }
        } catch {
          set({ user: null, accounts: [], currentAccount: null, isAuthenticated: false, isLoading: false })
        }
      },

      setCurrentAccount: (account: Account) => {
        set({ currentAccount: account })
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'ispot-auth-v3',
      partialize: (state) => ({
        // Only persist user info and current account, not loading/error state
        user: state.user,
        accounts: state.accounts,
        currentAccount: state.currentAccount,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
